import { ECP_TIERS, formatCount, formatRate, getEfmLimits, PROGRAMS } from "./programLimits";





export const MONTH_STATUSES = {
  COMPLIANT: "compliant",
  WARNING: "warning",
  UNDER_MONITORING: "under_monitoring",
  PENDING: "pending",
  NO_STATUS: "no_status"
}

export const STATUS_LABELS = {
  [MONTH_STATUSES.COMPLIANT]: "Compliant",
  [MONTH_STATUSES.WARNING]: "Warning",
  [MONTH_STATUSES.UNDER_MONITORING]: "Under monitoring",
  [MONTH_STATUSES.PENDING]: "Pending Data",
  [MONTH_STATUSES.NO_STATUS]: "No Status"
}

export const STATUS_CONDITIONS = [
  {
    status: MONTH_STATUSES.COMPLIANT,
    condition: "All conditions stayed below your limits — counts as a clean month."
  },
  {
    status: MONTH_STATUSES.WARNING,
    condition: "At least one condition reached 90% of its limit, but not all conditions were met."
  },
  {
    status: MONTH_STATUSES.UNDER_MONITORING,
    condition: "All conditions were met in the same month — a violation month."
  },
  {
    status: MONTH_STATUSES.PENDING,
    condition: "Not enough data to calculate this month — it does not count toward the program."
  },
  {
    status: MONTH_STATUSES.NO_STATUS,
    condition: "Month has not occurred yet, or is before your account was connected."
  }
]

export const STATUS_CONDITIONS_PANEL_TITLE = "All statuses and their conditions"

export const PROGRAM_SHORT_NAMES = {
  [PROGRAMS.VAMP]: "VAMP",
  [PROGRAMS.ECP]: "ECP",
  [PROGRAMS.EFM]: "EFM"
}

export const getMeaningLine = ({ status, program }) => {
  const name = PROGRAM_SHORT_NAMES[program]

  if (status === MONTH_STATUSES.UNDER_MONITORING)
    return `All limits were reached — this month counts toward ${name} enrollment.`

  if (status === MONTH_STATUSES.COMPLIANT)
    return "Your figures stayed within the limits — this month counts as a clean month."

  if (status === MONTH_STATUSES.WARNING)
    return "At least one limit was reached. All limits must be reached in the same month to enter the program."

  if (status === MONTH_STATUSES.PENDING)
    return "This month is still being calculated. The final status is set at the start of next month."

  return null
}

export const getNoStatusReason = ({ isFutureMonth }) =>
  isFutureMonth ? "This month has not started yet." : "No data is available for this month."

const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
]

/**
 * The finalization window named as the 1st to the 3rd of the month after `monthNumber` (§4 R11,
 * §5 R5). One helper, so the popover and the current-month slot always word it identically.
 */
export const getFinalizationWindow = (monthNumber) => MONTH_SHORT_NAMES[monthNumber % 12] ?? null

export const getClosingLine = ({
  status,
  program,
  isEnrolled,
  cleanStreakPosition,
  monthNumber
}) => {
  const name = PROGRAM_SHORT_NAMES[program]

  if (status === MONTH_STATUSES.UNDER_MONITORING)
    return "3 consecutive clean months are required to exit."

  if (status === MONTH_STATUSES.PENDING) {
    const nextMonth = getFinalizationWindow(monthNumber)

    return nextMonth
      ? `Final status on ${nextMonth} 1–3.`
      : "Final status is set between the 1st and the 3rd of the following month."
  }

  if (status !== MONTH_STATUSES.COMPLIANT && status !== MONTH_STATUSES.WARNING) return null

  if (!isEnrolled) return `All limits must be reached in the same month to enter ${name}.`

  if (cleanStreakPosition >= 3)
    return "3 consecutive clean months completed — the account exits the program."

  if (cleanStreakPosition) return `Month ${cleanStreakPosition} of 3 clean months toward exiting.`

  return "3 consecutive clean months are required to exit."
}

/**
 * The program's explanatory note, rendered between the limit list and the closing line (§4 R2).
 *
 * §4 R10 gives a note to EFM only. ECP additionally names its baseline tier: §4 R7 compares a month
 * that reached neither tier against ECM, and without saying so the merchant cannot tell why the
 * thresholds read 100 / 1.50% rather than 300 / 3.00%.
 */
export const getProgramNote = ({ program, month, region }) => {
  if (program === PROGRAMS.EFM)
    return `3DS usage is a protective condition: it is met only when usage is at or below ${formatRate(
      getEfmLimits(region).threeDs
    )}.`

  if (program === PROGRAMS.ECP && !month?.tier)
    return `No tier this month — measured against ECM, the baseline tier (${formatCount(
      ECP_TIERS.ECM.count
    )}+ chargebacks and rate ${formatRate(ECP_TIERS.ECM.rate)}+).`

  return null
}

export const CURRENT_MONTH_SLOT = {
  label: (monthName) => `${monthName} so far`,
  state: "Collecting data",
  counter: (reached, total) => `${reached} of ${total} conditions met`,
  note: (monthNumber) => {
    const nextMonth = getFinalizationWindow(monthNumber)

    return nextMonth
      ? `Final change will be around ${nextMonth} 1–3.`
      : "Final change will be around 1st–3rd of next month."
  }
}

export const PROGRAM_STATUS_LABEL = "Program status"

export const STATUS_CONDITIONS_TOGGLE = {
  label: "See more details",
  show: "Show the conditions behind this status",
  hide: "Hide the conditions"
}

export const TIER_CONTROL = {
  lead: "Mastercard assigns a tier only when the count condition and the rate condition are met together.",
  tiers: [
    {
      key: "ecm",
      label: "ECM",
      thresholds: "100+ chargebacks and rate 1.50%+",
      name: "Excessive Chargeback Merchant",
      consequences: "Monitoring starts; fees from the second consecutive month."
    },
    {
      key: "hecm",
      label: "HECM",
      thresholds: "300+ chargebacks and rate 3.00%+",
      name: "High Excessive Chargeback Merchant",
      consequences: "Higher fees and faster escalation."
    }
  ]
}

export const LIMIT_TOOLTIPS = {
  vampZone:
    "Rate limit for your region. VAMP enrollment also requires the incident limit to be exceeded in the same month.",
  efmZone:
    "Fraud rate limit for your region. EFM enrollment requires all four conditions to be met in the same month.",
  regionUnavailable: "Region data unavailable. Value will update after next sync."
}

export const HIGHER_IS_BETTER = {
  marker: "Higher is better",
  tooltip:
    "Higher 3DS usage lowers your EFM risk. The EFM condition is met only when usage is at or below the limit."
}

export const PROGRAM_LIMITS_LABEL = "Program limits"

export const ESTIMATED_STATUS_TOOLTIP =
  "Values are estimated from the data available to us. The official program status comes from your acquirer or processor."

export const MONTH_SEGMENTS = {
  closed: "Closed months — counted",
  inProgress: "In progress",
  notStarted: "Not started"
}
